import React, { useEffect, useState } from "react";
import {
  createUserWithEmailAndPassword,
  updateProfile,
  isSignInWithEmailLink,
  signInWithEmailLink,
  updatePassword,
  sendEmailVerification,
} from "firebase/auth";
import { auth, db } from "../firebaseConfig";
import {
  collection,
  addDoc,
  query,
  where,
  getDocs,
  updateDoc,
} from "firebase/firestore";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

const Register = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [usingInviteLink, setUsingInviteLink] = useState(false);
  const navigate = useNavigate();
  const { setUser } = useAuth();

  // Detecta si venimos desde el enlace de invitación (email link)
  useEffect(() => {
    const url = window.location.href;
    const viaLink = isSignInWithEmailLink(auth, url);
    setUsingInviteLink(viaLink);

    if (viaLink) {
      // 1) intenta tomar ?email=... del enlace (por si abren en otro dispositivo)
      try {
        const p = new URL(url).searchParams.get("email");
        if (p) {
          setEmail(decodeURIComponent(p));
          return;
        }
      } catch {}

      // 2) si no viene en la URL, usa el respaldo local (mismo dispositivo)
      const storedEmail = window.localStorage.getItem("inviteEmail");
      if (storedEmail && !email) setEmail(storedEmail);
    }
  }, []); // eslint-disable-line

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validaciones comunes
    if (!firstName || !lastName || !email || !password || !confirmPassword) {
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text: "Todos los campos son obligatorios",
      });
      return;
    }
    if (password.length < 6) {
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text: "La contraseña debe tener al menos 6 caracteres",
      });
      return;
    }
    if (password !== confirmPassword) {
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text: "Las contraseñas no coinciden",
      });
      return;
    }

    try {
      const firstNameOnly = firstName.trim().split(" ")[0];
      const lastNameOnly = lastName.trim().split(" ")[0];
      const displayName = `${firstNameOnly} ${lastNameOnly}`.trim();

      if (usingInviteLink) {
        // *** FLUJO CON INVITACIÓN (Email Link) ***
        // 1) Completar el sign-in con el link
        const result = await signInWithEmailLink(
          auth,
          email,
          window.location.href
        );

        // 2) Poner contraseña al usuario (opcional pero típico en tu flujo)
        await updatePassword(result.user, password);

        // 3) Nombre visible
        await updateProfile(result.user, { displayName });

        // 4) Marcar registrado en Firestore y guardar nombre/apellido si existe
        const q = query(
          collection(db, "usuarios"),
          where("email", "==", email.toLowerCase())
        );
        const snap = await getDocs(q);
        if (!snap.empty) {
          await updateDoc(snap.docs[0].ref, {
            nombre: firstName,
            apellido: lastName,
            email: email.toLowerCase(),
            registrado: true,
          });
        } else {
          await addDoc(collection(db, "usuarios"), {
            nombre: firstName,
            apellido: lastName,
            email: email.toLowerCase(),
            rol: "Ventas",
            registrado: true,
          });
        }

        // (Email link ya verifica el correo; no necesitas sendEmailVerification)
        window.localStorage.removeItem("inviteEmail");

        Swal.fire({
          icon: "success",
          title: "¡Registro completado!",
          timer: 1200,
          showConfirmButton: false,
        });
        setUser(result.user);
        navigate("/");
      } else {
        // *** FLUJO NORMAL (sin enlace) ***
        const cred = await createUserWithEmailAndPassword(
          auth,
          email,
          password
        );
        await updateProfile(cred.user, { displayName });
        await sendEmailVerification(auth.currentUser);

        const q = query(
          collection(db, "usuarios"),
          where("email", "==", email.toLowerCase())
        );
        const snap = await getDocs(q);
        if (!snap.empty) {
          await updateDoc(snap.docs[0].ref, {
            nombre: firstName,
            apellido: lastName,
            registrado: true,
          });
        } else {
          await addDoc(collection(db, "usuarios"), {
            nombre: firstName,
            apellido: lastName,
            email: email.toLowerCase(),
            rol: "Ventas",
            registrado: true,
          });
        }

        Swal.fire({
          icon: "success",
          title: "¡Usuario registrado!",
          text: "Te enviamos un correo para verificar tu cuenta.",
          timer: 1500,
        });

        setUser(cred.user);
        navigate("/");
      }
    } catch (error) {
      console.error("Error registrando usuario:", error);
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text: error?.message || "Hubo un error al registrar el usuario.",
      });
    }
  };

  return (
    <div className="register-container">
      <div className="register-form">
        <h1>Regístrate</h1>
        <p style={{ color: "#555" }}>
          {usingInviteLink
            ? "Completa tus datos para activar tu cuenta."
            : "Completa los siguientes campos para crear tu cuenta."}
        </p>
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: "10px" }}>
            <input
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="Nombre(s)"
              style={{ marginRight: "10px", width: "calc(50% - 5px)" }}
              required
            />
            <input
              type="text"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              placeholder="Apellido(s)"
              style={{ width: "calc(50% - 5px)" }}
              required
            />
          </div>

          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email"
            required
          />

          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={usingInviteLink ? "Elige tu contraseña" : "Contraseña"}
            required
          />

          <input
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="Repetir Contraseña"
            required
          />

          <button
            style={{ borderRadius: "30px", marginTop: "10px" }}
            type="submit"
          >
            Continuar
          </button>
        </form>

        <div className="register-containerLogin">
          <p style={{ display: "inline-block", marginRight: "5px" }}>
            ¿Ya tienes una cuenta?
          </p>
          <span onClick={() => navigate("/login")} className="register-link">
            Login
          </span>
        </div>
      </div>
    </div>
  );
};

export default Register;
